import { useCallback, useEffect, useState } from "react";

import {
  applyAdaptationPreview,
  fetchAdaptationPreview,
} from "@/api/adaptiveTrainingApi";
import { notifyActivePlanChanged } from "@/lib/activePlanSync";

const SPORT_NOUN = {
  cycling: "Ride",
  running: "Run",
  swimming: "Swim",
  walking: "Walk",
  hiking: "Hike",
  rowing: "Row",
  strength: "Strength session",
};

export const athleteLocalDate = (timezone, now = new Date()) => {
  const zone = timezone || "UTC";
  try {
    return new Intl.DateTimeFormat("en-CA", {
      timeZone: zone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(now);
  } catch (_err) {
    return new Intl.DateTimeFormat("en-CA", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(now);
  }
};

const sportNoun = (sport) => {
  const key = String(sport || "").toLowerCase();
  return SPORT_NOUN[key] || (key ? key.replace(/_/g, " ") : "Session");
};

const Side = ({ title, testId, date, weekday, workout, duration, sport }) => (
  <div data-testid={testId} className="min-w-0 rounded-md border border-dark-border p-3">
    <p className="text-[10px] uppercase tracking-[0.2em] text-dark-text-muted">{title}</p>
    <p className="mt-1 text-sm text-dark-text-primary" data-testid={`${testId}-when`}>
      {[weekday, date].filter(Boolean).join(" ")}
    </p>
    <p className="text-sm font-medium text-dark-text-primary" data-testid={`${testId}-workout`}>
      {workout || "Workout"}
    </p>
    <p className="text-sm text-dark-text-secondary" data-testid={`${testId}-duration`}>
      {duration != null ? `${duration} min` : "Duration unchanged"}
    </p>
    <p className="text-sm text-dark-text-secondary" data-testid={`${testId}-sport`}>
      {sportNoun(sport)}
    </p>
  </div>
);

const messageFrom = (err) => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (detail && typeof detail === "object" && detail.message) return detail.message;
  return err?.message || "Could not update the plan.";
};

export const AdaptiveTrainingCard = ({ timezone, asOf, onPlanChanged }) => {
  const planDate = asOf || athleteLocalDate(timezone);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [missingPlan, setMissingPlan] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [staleNote, setStaleNote] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchAdaptationPreview(planDate);
      setPreview(data);
      setMissingPlan(false);
      setError(null);
      setConfirming(false);
    } catch (err) {
      if (err?.response?.status === 404) {
        setPreview(null);
        setMissingPlan(true);
        setError(null);
      } else {
        setError(messageFrom(err));
      }
    } finally {
      setLoading(false);
    }
  }, [planDate]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await fetchAdaptationPreview(planDate);
        if (cancelled) return;
        setPreview(data);
        setMissingPlan(false);
        setError(null);
        setConfirming(false);
      } catch (err) {
        if (cancelled) return;
        if (err?.response?.status === 404) {
          setPreview(null);
          setMissingPlan(true);
          setError(null);
        } else {
          setError(messageFrom(err));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [planDate]);

  const change = preview?.changes?.length === 1 ? preview.changes[0] : null;
  const canApply = preview?.status === "READY_TO_APPLY" && Boolean(change);

  const confirmApply = async () => {
    if (!canApply || busy) return;
    const fingerprint = preview.preview_fingerprint;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const result = await applyAdaptationPreview({
        asOf: planDate,
        previewFingerprint: fingerprint,
      });
      if (result?.status === "APPLIED" || result?.status === "ALREADY_APPLIED") {
        setNotice(result.status === "ALREADY_APPLIED"
          ? "This change is already on your plan."
          : "Your plan was updated.");
        setStaleNote(null);
        setConfirming(false);
        notifyActivePlanChanged({ source: "stage18-adaptation" });
        if (onPlanChanged) await onPlanChanged();
        await load();
        return;
      }
      if (result?.status === "STALE_PREVIEW") {
        setConfirming(false);
        setNotice(null);
        setStaleNote("Your plan changed, so this recommendation was refreshed. Review it again before applying.");
        await load();
        return;
      }
      setNotice(null);
      setError(result?.why || "The plan was not changed.");
      setConfirming(false);
    } catch (err) {
      setNotice(null);
      setError(messageFrom(err));
      setConfirming(false);
    } finally {
      setBusy(false);
    }
  };

  if (loading && !preview && !error) {
    return (
      <p className="text-sm text-dark-text-muted" data-testid="adaptation-loading">
        Checking your plan…
      </p>
    );
  }
  if (missingPlan) return null;
  if (error && !preview) {
    return (
      <p role="alert" className="text-sm text-analytics-amber" data-testid="adaptation-error">
        {error}
      </p>
    );
  }
  if (!preview) return null;

  const unchanged = preview.unchanged_summary?.statement;

  return (
    <section
      aria-label="Plan adjustment"
      data-testid="adaptation-card"
      className="space-y-3"
    >
      {notice && (
        <p
          className="rounded-md border border-analytics-green/40 bg-analytics-green/10 px-3 py-2 text-sm text-analytics-green"
          data-testid="adaptation-success"
        >
          {notice}
        </p>
      )}
      {staleNote && (
        <p
          role="status"
          className="rounded-md border border-analytics-amber/40 bg-analytics-amber/10 px-3 py-2 text-sm text-analytics-amber"
          data-testid="adaptation-stale"
        >
          {staleNote}
        </p>
      )}
      {error && (
        <p role="alert" className="text-sm text-analytics-amber" data-testid="adaptation-error">
          {error}
        </p>
      )}

      {preview.status === "NO_CHANGE" && (
        <p className="text-sm text-dark-text-muted" data-testid="adaptation-no-change">
          Your current training does not require a plan change.
        </p>
      )}

      {preview.status === "REVIEW_REQUIRED" && (
        <div data-testid="adaptation-review">
          <p className="text-sm text-dark-text-primary">
            Apex needs more certainty before changing this part of your plan.
          </p>
          {preview.explanation && (
            <p className="mt-1 text-sm text-dark-text-secondary" data-testid="adaptation-why">
              {preview.explanation}
            </p>
          )}
        </div>
      )}

      {preview.status === "UNABLE_TO_APPLY" && (
        <div data-testid="adaptation-unable">
          <p className="text-sm text-dark-text-secondary" data-testid="adaptation-why">
            {preview.explanation || "Apex could not find a safe change."}
          </p>
        </div>
      )}

      {preview.status === "READY_TO_APPLY" && (
        <div className="app-card p-4 sm:p-5" data-testid="adaptation-ready">
          <h2 className="section-title">Suggested plan change</h2>
          <p className="mt-2 text-sm text-dark-text-secondary" data-testid="adaptation-why">
            {preview.explanation}
          </p>
          {change && (
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Side
                title="Before"
                testId="adaptation-before"
                date={change.old_date}
                weekday={change.old_weekday}
                workout={change.old_workout?.label || change.old_workout?.template_code}
                duration={change.old_duration}
                sport={change.sport}
              />
              <Side
                title="After"
                testId="adaptation-after"
                date={change.new_date}
                weekday={change.new_weekday}
                workout={change.new_workout?.label || change.new_workout?.template_code || "Removed"}
                duration={change.new_duration}
                sport={change.sport}
              />
            </div>
          )}
          {!change && (
            <p className="mt-3 text-sm text-dark-text-secondary" data-testid="adaptation-multi">
              Apex will not apply more than one change at a time.
            </p>
          )}
          {unchanged && (
            <p className="mt-3 text-sm text-dark-text-muted" data-testid="adaptation-unchanged">
              {unchanged}
            </p>
          )}
          {canApply && !confirming && (
            <button
              type="button"
              className="btn-primary-app mt-4 px-3 py-2 text-[10px] tracking-widest uppercase"
              data-testid="adaptation-apply"
              onClick={() => setConfirming(true)}
            >
              Apply change
            </button>
          )}
          {canApply && confirming && (
            <div className="mt-4 space-y-2" data-testid="adaptation-confirm">
              <p className="text-sm text-dark-text-primary">
                Apply this one change to your plan?
              </p>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={busy}
                  className="btn-primary-app px-3 py-2 text-[10px] tracking-widest uppercase"
                  data-testid="adaptation-confirm-yes"
                  onClick={confirmApply}
                >
                  Confirm change
                </button>
                <button
                  type="button"
                  disabled={busy}
                  className="btn-secondary-app px-3 py-2 text-[10px] tracking-widest uppercase"
                  data-testid="adaptation-confirm-cancel"
                  onClick={() => setConfirming(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
};
